// ─── Certifications Route (ADR 0030, issue #225) ───
// /app/certifications — new top-level sidebar section (ADR 0030 D7), scoped to the current
// workspace, with Year and List as tabs. Everyone in the workspace sees everyone's
// certifications (ADR 0030 D8) — no role gate on this route, unlike /app/knowledge.
//
// `view` mirrors routes/app/knowledge.tsx's own tab param. GHE #927 flipped the DEFAULT from
// 'year' to 'list': the year canvas answers "what happened on 14 June", and nobody asks that —
// the question people actually arrive with is "what expires soon, and whose is it", which the
// list answers directly. Both tabs remain; only which one opens changed. The list filters (personId/typeId/status/
// includeDisabled) live in the URL too, matching the house `useTaskFilters.ts` precedent
// (filters are shareable-by-link) — `.catch(undefined)` on every field so a stale/garbage deep
// link degrades to "filter absent" rather than throwing (routes/app/knowledge.tsx's own rule).
//
// `spaceId` (ADR 0031 D7) narrows the roster to one space team's people, on BOTH tabs — it
// lives here rather than as separate local state per panel so a filtered link works from
// either the Year or the List view (§7 CertificationListPanel.tsx doc comment).

import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { CERTIFICATION_STATUSES } from '@a8-planner/shared/types/certification';
import { CertificationsPage } from '../../features/certifications/CertificationsPage';

export const certificationsSearchSchema = z.object({
  // GHE #929 adds 'calendar' — the whole-year month grid.
  view: z.enum(['year', 'list', 'calendar']).optional().catch(undefined),
  personId: z.string().uuid().optional().catch(undefined),
  typeId: z.string().uuid().optional().catch(undefined),
  /**
   * GHE #835 — three states, not two: absent = "not chosen yet" (defaults to the space you are
   * in), `'all'` = explicitly every space, a uuid = that space. A bare `z.string().uuid()` here
   * would drop `'all'` through `.catch(undefined)` and silently turn an explicit "All spaces"
   * back into "not chosen", which is the exact bug the sentinel exists to prevent.
   */
  spaceId: z
    .union([z.literal('all'), z.string().uuid()])
    .optional()
    .catch(undefined),
  status: z.enum(CERTIFICATION_STATUSES).optional().catch(undefined),
  /** GHE #927 — the urgency bucket, as a day threshold. `.catch(undefined)` means a junk value
   *  falls back to "no bucket" (Todas) rather than 400ing the surface. */
  expiringWithinDays: z.coerce.number().int().positive().optional().catch(undefined),
  includeDisabled: z.boolean().optional().catch(undefined),
});

export const Route = createFileRoute('/app/certifications')({
  validateSearch: certificationsSearchSchema,
  component: CertificationsRoute,
});

// GHE #452 — no Breadcrumb here. This is a top-level destination with no path
// above it, so the only segment it could show is "Certificaciones", which is
// exactly what CertificationsPage already renders as its <h1>.
function CertificationsRoute(): React.ReactElement {
  return <CertificationsPage />;
}
